import React, { useState, useEffect } from 'react';
import styled, { css } from 'styled-components';
import useOverlayOutsideClick from '@westwing/ui-kit/Tools/hooks/useOverlayOutsideClick';
import { header6, caption1 } from '@westwing/ui-kit/typography';
import { forDesktop, forTablet } from '@westwing/ui-kit/breakPoints';
import Tag from '@westwing/ui-kit/Icons/TagIcon';
import Stars from '@westwing/ui-kit/Icons/StarsIcon';
import { useHeaderContext } from './HeaderContext';

const arrowSpace = 20;
const arrowSize = 17;
const contentSpace = Math.round(arrowSpace + arrowSize / 2);
enum Tabs {
    Shop = 'SHOP',
    Club = 'CLUB',
}

export const OnboardingWrapper = styled.div<{ $tooltipFor: Tabs.Shop | Tabs.Club }>`
    ${({ $tooltipFor }) =>
        $tooltipFor === Tabs.Shop
            ? css`
                  left: 50%;
                  transform: translateX(-50%);
              `
            : css`
                  right: calc(50% - ${contentSpace}px);
                  ${forDesktop`
                    right: auto;
                    left: calc(50% - ${contentSpace}px);
                `}
              `}
    box-sizing: border-box;
    z-index: ${({ theme }) => theme.zIndexContentOverlay};
    width: 248px;
    position: absolute;
    top: 54px;

    ${forTablet`
        top: 58px;
    `}

    ${forDesktop`
        top: 40px;
    `}
`;

export const OnboardingContentOverlay = styled.div`
    box-sizing: border-box;
    position: relative;
    width: 100%;
`;

export const OnboardingInsideWrapper = styled.div<{ $tooltipFor: Tabs.Shop | Tabs.Club }>`
    background: ${({ theme }) => theme.colors.tooltipBackground};
    border: 1px solid ${({ theme }) => theme.colors.tooltipBorder};
    box-sizing: border-box;
    padding: 16px 20px;

    :before {
        content: '';
        ${({ $tooltipFor }) =>
            $tooltipFor === Tabs.Shop
                ? css`
                      left: 0;
                      right: 0;
                      margin: 0 auto;
                  `
                : css`
                      right: ${arrowSpace}px;
                      ${forDesktop`
                        right: auto;
                        left: ${arrowSpace}px;
                    `}
                  `}
        background: ${({ theme }) => theme.colors.tooltipBackground};
        border-top: 1px solid ${({ theme }) => theme.colors.tooltipBorder};
        border-left: 1px solid ${({ theme }) => theme.colors.tooltipBorder};
        width: ${arrowSize}px;
        height: ${arrowSize}px;
        position: absolute;
        top: -8px;
        transform: rotate(45deg);
        box-sizing: border-box;
    }
`;

export const OnboardingTooltipHeader = styled.div`
    display: flex;
    align-items: center;
    margin-bottom: 2px;
    box-sizing: border-box;
`;

export const OnboardingTooltipHeaderText = styled.p`
    ${header6}
    color: ${({ theme }) => theme.colors.charcoal100};
    font-family: ${({ theme }) => theme.fonts.fontChronicleRoman};
    white-space: normal;
    margin: 0 0 0 8px;
    line-height: normal;
    box-sizing: border-box;
`;

export const OnboardingTooltipText = styled.p`
    ${caption1}
    color: ${({ theme }) => theme.colors.charcoal100};
    font-family: ${({ theme }) => theme.fonts.fontBrandonRegular};
    white-space: normal;
    margin: 0;
    box-sizing: border-box;
`;

export const OnboardingTagIcon = styled(Tag)`
    min-width: fit-content;
    color: ${({ theme }) => theme.colors.charcoal100};
`;

export const OnboardingStarsIcon = styled(Stars)`
    min-width: fit-content;
    color: transparent;
    stroke: ${({ theme }) => theme.colors.charcoal100};
`;

const OnboardingTooltip = (): React.ReactElement | null => {
    const { t, app } = useHeaderContext();
    const [isActive, setIsActive] = useState<boolean>(false);

    const tooltipData =
        app === Tabs.Club
            ? {
                  tooltipFor: Tabs.Shop,
                  headerText: t('OwwHeader_Shop tooltip headline'),
                  text: t('OwwHeader_Shop tooltip text'),
                  TooltipIcon: OnboardingStarsIcon,
              }
            : {
                  tooltipFor: Tabs.Club,
                  headerText: t('OwwHeader_Club tooltip headline'),
                  text: t('OwwHeader_Club tooltip text'),
                  TooltipIcon: OnboardingTagIcon,
              };

    const { tooltipFor, headerText, text, TooltipIcon } = tooltipData;

    const ref = useOverlayOutsideClick(() => setIsActive(false), true);

    useEffect(() => {
        if (document.cookie.includes('has_user_seen_onboarding_tooltip_before=1') || window.scrollY > 0) {
            return undefined;
        }

        setIsActive(true);
        document.cookie = 'has_user_seen_onboarding_tooltip_before=1;path=/';

        window.addEventListener('scroll', () => setIsActive(false));
        return () => {
            window.removeEventListener('scroll', () => setIsActive(false));
        };
    }, []);

    if (!isActive || !app) {
        return null;
    }

    return (
        <OnboardingWrapper ref={ref} $tooltipFor={tooltipFor}>
            <OnboardingContentOverlay>
                <OnboardingInsideWrapper $tooltipFor={tooltipFor}>
                    <OnboardingTooltipHeader>
                        <TooltipIcon />
                        <OnboardingTooltipHeaderText>{headerText}</OnboardingTooltipHeaderText>
                    </OnboardingTooltipHeader>
                    <OnboardingTooltipText>{text}</OnboardingTooltipText>
                </OnboardingInsideWrapper>
            </OnboardingContentOverlay>
        </OnboardingWrapper>
    );
};

export default OnboardingTooltip;
